import { JsonEditor } from "@/common";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import { Typography } from "@mui/material";
import type { FileAttachment } from "@packages/commons";
import React, { useMemo } from "react";
import { SpreadsheetPreview } from "../SpreadsheetPreview/SpreadsheetPreview";
import { downloadFileInBrowser } from "./downloadFileInBrowser";

const BINARY_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "application/zip",
  "application/x-zip-compressed",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const getFileType = (filename: string, data: string): { type: string; mimeType?: string } => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";

  // Try to extract MIME type from data URL
  const dataUrlMatch = data.match(/^data:([^;]+);base64,/);
  const mimeType = dataUrlMatch ? dataUrlMatch[1] : undefined;

  if (ext === "csv" || mimeType === "text/csv") {
    return { type: "csv", mimeType: "text/csv" };
  }
  if (ext === "xlsx" || ext === "xls" || mimeType?.includes("spreadsheet")) {
    return { type: "xlsx", mimeType };
  }
  if (ext === "json" || mimeType === "application/json") {
    return { type: "json", mimeType: "application/json" };
  }
  if (mimeType && BINARY_TYPES.includes(mimeType)) {
    return { type: "binary", mimeType };
  }
  if (mimeType?.startsWith("text/")) {
    return { type: "text", mimeType };
  }

  // Default to text for unknown types
  return { type: "text", mimeType: mimeType || "text/plain" };
};

export interface FilePreviewProps {
  attachment: FileAttachment;
  className?: string;
}

export const FilePreview = (props: FilePreviewProps) => {
  const { attachment, className } = props;
  const fileType = useMemo(() => {
    if (attachment.data) {
      return getFileType(attachment.name, attachment.data);
    }

    if (attachment.fileType) {
      return getFileType(attachment.name, `data:${attachment.fileType};base64,`);
    }

    return getFileType(attachment.name, "");
  }, [attachment]);

  const handleDownload = () => {
    try {
      if (attachment.data) {
        downloadFileInBrowser(attachment.data, attachment.name);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const usesBorderHeader = fileType.type === "csv" || fileType.type === "json" || fileType.type === "text";
  const containerClassName = `flex flex-col h-full ${usesBorderHeader ? "" : "p-4"} ${className || ""}`;
  const headerClassName = usesBorderHeader ? "px-4 py-2 border-b border-border" : "mb-4";
  const headerTitle = attachment.label || attachment.name;
  const showLabel = Boolean(attachment.label);

  const data = attachment.data || "";
  const content = useMemo(() => {
    let parsedContent = data;
    try {
      // Extract base64 data if it's a data URL
      const base64Match = data.match(/^data:([^;]+);base64,(.+)$/);
      if (base64Match) {
        const decoded = atob(base64Match[2]);
        if (fileType.type === "json") {
          // Try to parse as JSON
          try {
            const parsed = JSON.parse(decoded);
            parsedContent = JSON.stringify(parsed, null, 2);
          } catch {
            parsedContent = decoded;
          }
        } else {
          parsedContent = decoded;
        }
      } else if (fileType.type === "json") {
        // Try to parse as JSON if it's plain text
        try {
          const parsed = JSON.parse(data);
          parsedContent = JSON.stringify(parsed, null, 2);
        } catch {
          parsedContent = data;
        }
      }
    } catch (error) {
      console.error("Error processing file content:", error);
    }

    return parsedContent;
  }, [data, fileType.type]);

  const bodyContent = useMemo(() => {
    if (fileType.type === "csv") {
      return (
        <div className="flex-1 overflow-hidden">
          <SpreadsheetPreview data={data} />
        </div>
      );
    }

    if (fileType.type === "xlsx") {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Excel file preview is not yet supported. Please download to view.
        </Typography>
      );
    }

    if (fileType.type === "binary") {
      return (
        <div className="flex-1 flex items-center justify-center">
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Binary file type ({fileType.mimeType || "unknown"}) cannot be previewed.
            <br />
            Please download to view.
          </Typography>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-hidden">
        <JsonEditor
          value={content}
          readOnly={true}
          withCopy={true}
        />
      </div>
    );
  }, [content, data, fileType.mimeType, fileType.type]);

  return (
    <div className={containerClassName}>
      <div className={`flex items-center justify-between ${headerClassName}`}>
        <div className="flex flex-col overflow-hidden mr-4">
          <Typography
            variant="subtitle2"
            noWrap
          >
            {headerTitle}
          </Typography>
          {showLabel ? (
            <Typography
              variant="caption"
              color="textSecondary"
              noWrap
            >
              {attachment.name}
            </Typography>
          ) : null}
        </div>
        <Button 
          variant="ghost"
          onClick={handleDownload}
          className="shrink-0"
         label="Download">
          <Icon
            name="download"
            size={16}
            className="mr-2"
          />
          Download
        </Button>
      </div>
      {bodyContent}
    </div>
  );
};
